import { ExternalLink } from "lucide-react";
import { fetch } from "../../../lib/fetch";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { YachtDetails } from "../../Yachts/model";
import { useEffect, useState } from "react";
import { Separator } from "../../../components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { cn } from "../../../lib/utils";
import { Link } from "react-router-dom";
import { getConfig } from "@/src/config";
import { useToast } from "../../../components/ui/use-toast";
import YachtLabel from "@/src/components/ui/yacht-label";
import { Availability, fetchAvailability } from "./useOldSearch";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { format } from "path";

type YachtDetailsModal = {
  yachtDetailsId: string | null;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const YachtDetailsModal = ({ yachtDetailsId, open, setOpen }: YachtDetailsModal) => {
  const [yacht, setYacht] = useState<YachtDetails | null>(null);
  const [availability, setAvailability] = useState<Availability[]>([]);

  const toast = useToast();

  // Helper to get the website URL from runtime config
  function getWebsiteUrl(): string {
    try {
      const { WEBSITE_URL } = getConfig();
      return WEBSITE_URL;
    } catch (err) {
      console.error("Config not loaded yet", err);
      return ""; // fallback or throw error if preferred
    }
  }

  useEffect(() => {
    async function getYacht() {
      toast.toast({
        title: "Fetching data...",
        duration: 100000,
      });
      return await fetch<{ status: string; data: YachtDetails }>("/api/yachts/yacht/" + yachtDetailsId)
        .then((res) => {
          setYacht(res.data);
          toast.dismiss();
        })
        .catch(() =>
          toast.toast({
            title: "Error fetching the data",
            duration: 2000,
          }),
        );
    }
    async function getAvailability() {
      setAvailability([]);
      if (yachtDetailsId) {
        return fetchAvailability(yachtDetailsId).then(setAvailability);
      }
    }
    if (yachtDetailsId) {
      getYacht();
      getAvailability();
    }
  }, []);

  if (!yacht) return null;

  const clientPrice = 0;
  const location = yacht.location.names;

  let minDuration: number | "any" = "any";

  if (yacht.provider === "BM") {
    minDuration = yacht.meta.bmPriceInfo?.minDuration ?? "any";
  }

  if (yacht.provider === "NS") {
    minDuration = yacht.meta.nsCheckIn?.[0].minDuration ?? "any";
  }

  if (yacht.provider === "Helm") {
    minDuration = yacht.meta.helmCheckIn?.minDuration ?? "any";
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[720px] max-h-[calc(100%-60px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-semi-bold">Yacht details</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 overflow-hidden relative">
          <Button variant="link" className="absolute top-0 right-0" asChild>
            <Link to={`/yachts/${yacht.id}?step=Basic details`}>Edit</Link>
          </Button>
          <div className="flex gap-4">
            <img
              width={178}
              height={108}
              className="object-cover rounded-[5px] max-h-[108px]"
              src={yacht.media?.[0]}
              alt={yacht.name}
            />
            <div className="flex flex-col items-start">
              <YachtLabel apiDateUpdated={yacht.apiDateUpdated} apiStatus={yacht.apiStatus} />
              <Button asChild className="text-black p-0 h-[28px]" variant="link">
                <a target="_blank" rel="noreferrer" href={`${getWebsiteUrl()}/yacht/${yacht.slug}`}>
                  “{yacht.name}”<ExternalLink className="ml-1.5" size={16} />
                </a>
              </Button>
              <span className="text-base mt-1.5">
                {yacht.model?.name} | {yacht.year}
              </span>
              <span className="text-sm mt-1.5">
                {!!yacht.maxPeople && `${yacht.maxPeople} people`} {!!yacht.cabins && `• ${yacht.cabins} cabins`}{" "}
                {!!yacht.bathrooms && `• ${yacht.bathrooms} bathrooms`}
              </span>
              <div className="flex flex-col mt-2">
                <span className="text-xs text-slate-500 font-medium">Operator</span>
                <span className="text-sm">{yacht.operator?.name}</span>
              </div>
              <div className="flex flex-col mt-2">
                <span className="text-xs text-slate-500 font-medium">Boat type</span>
                <span className="text-sm">{yacht.boatType}</span>
              </div>
              <div className="flex flex-col mt-2">
                <span className="text-xs text-slate-500 font-medium">Minimum trip duration</span>
                <span className="text-sm">
                  {minDuration} {minDuration !== "any" ? (+minDuration > 1 ? "days" : "day") : null}
                </span>
              </div>
              {yacht.ratingHelm && (
                <div className="flex flex-col mt-2">
                  <span className="text-xs text-slate-500 font-medium">Helm rating</span>
                  <span className="text-sm">{yacht.ratingHelm}</span>
                </div>
              )}
              {yacht.ratingAnchor && (
                <div className="flex flex-col mt-2">
                  <span className="text-xs text-slate-500 font-medium">Anchor rating</span>
                  <span className="text-sm">{yacht.ratingAnchor}</span>
                </div>
              )}
              <div className="flex flex-col mt-2">
                <span className="text-xs text-slate-500 font-medium">Brand</span>
                <p className="text-sm">
                  {[yacht.visibleInAnchor && "Anchor", yacht.visibleInHelm && "Helm"].filter(Boolean).join(", ")}
                </p>
              </div>
              <div className="flex flex-col mt-2">
                <span className="text-xs text-slate-500 font-medium">Description</span>
                <p className="text-sm">{yacht.description}</p>
              </div>
            </div>
          </div>
          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Amenities`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Equipment</h4>
            <ul className="grid grid-cols-2">
              {yacht.amenities?.slice(0, 8).map((am, index) => (
                <li key={index} title={am.text} className="flex flex-col px-4 py-3">
                  <span className="text-base flex gap-2">
                    <span className="line-clamp-1 break-all">{am.text}</span>
                    {am.included && <span className="text-xs px-2 py-1 bg-blue-50 rounded-[25px]">Included</span>}{" "}
                  </span>
                  {am.paid && (
                    <span className="text-slate-700 text-sm">
                      + {evalExtraPriceSimplified(am.formula, location, clientPrice)}
                    </span>
                  )}
                  {am.paid_locally && <span className="text-slate-700 text-sm">paid locally</span>}
                </li>
              ))}
            </ul>
            <Dialog>
              <DialogTrigger>
                <Button className="flex justify-start mt-2" variant="link">
                  Show all
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-3xl">Equipment</DialogTitle>
                </DialogHeader>
                <ul className="grid grid-cols-2">
                  {yacht.amenities?.map((am, index) => (
                    <li key={index} title={am.text} className="flex flex-col px-3 py-2">
                      <span className="text-base flex gap-2">
                        <span className="line-clamp-1 break-all">{am.text}</span>
                        {am.included && (
                          <span className="text-xs px-2 py-1 bg-blue-50 rounded-[25px]">Included</span>
                        )}{" "}
                      </span>
                      {am.paid && (
                        <span className="text-slate-700 text-sm">
                          + {evalExtraPriceSimplified(am.formula, location, clientPrice)}
                        </span>
                      )}
                      {am.paid_locally && <span className="text-slate-700 text-sm">paid locally</span>}
                    </li>
                  ))}
                </ul>
                <DialogFooter></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Amenities`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Specification</h4>

            <ul className="grid grid-cols-2">
              {yacht.specs?.slice(0, 8).map((spec, index) => (
                <li key={index} className="flex flex-col px-4 py-3">
                  <span className="text-base flex gap-2">
                    <span className="font-medium break-words">{"Name" in spec ? spec.Name : spec.name}:</span>
                    <span>{"Value" in spec ? spec.Value : spec.value}</span>
                  </span>
                </li>
              ))}
            </ul>

            {yacht.specs?.length > 8 && (
              <Dialog>
                <DialogTrigger>
                  <Button className="flex justify-start mt-2" variant="link">
                    Show all
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-3xl">Specification</DialogTitle>
                  </DialogHeader>
                  <ul className="grid grid-cols-2">
                    {yacht.specs.map((spec, index) => (
                      <li key={index} className="flex flex-col px-3 py-2">
                        <span className="text-base flex gap-2">
                          <span className="font-medium break-words">{"Name" in spec ? spec.Name : spec.name}:</span>
                          <span>{"Value" in spec ? spec.Value : spec.value}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <DialogFooter />
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Basic details`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Available in databases</h4>
            <span className="text-base font-medium">{yacht.provider}</span>
          </div>
          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Specification`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Additional specifications</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col text-base">
                <span>Length overall</span>
                <span className="font-medium">{yacht.length}</span>
              </div>
              <div className="flex flex-col text-base">
                <span>Year</span>
                <span className="font-medium">{yacht.year}</span>
              </div>
              <div className="flex flex-col text-base">
                <span>Beam</span>
                <span className="font-medium">
                  {(() => {
                    const beamSpec = yacht.specs.find((item: any) => item.Name === "Beam" || item.name === "Beam");

                    if (!beamSpec) return "N/A";

                    if ("Value" in beamSpec) return beamSpec.Value;
                    if ("value" in beamSpec) return beamSpec.value;

                    return "N/A";
                  })()}
                </span>
              </div>
              <div className="flex flex-col text-base">
                <span>No. of cabins</span>
                <span className="font-medium">{yacht.cabins}</span>
              </div>
              <div className="flex flex-col text-base">
                <span>No. of berths</span>
                <span className="font-medium">TODO</span>
              </div>
              <div className="flex flex-col text-base">
                <span>No. of bathrooms</span>
                <span className="font-medium">{yacht.bathrooms}</span>
              </div>
              <div className="flex flex-col text-base">
                <span>Manufacturer</span>
                <span className="font-medium">{yacht.builder}</span>
              </div>
              <div className="flex flex-col text-base">
                <span>Mainsail</span>
                <span className="font-medium">
                  {(() => {
                    const mainsailTypeSpec = yacht.specs.find(
                      (item: any) => item.Name === "Mainsail Type" || item.name === "Mainsail Type",
                    );

                    if (!mainsailTypeSpec) return "N/A";

                    if ("Value" in mainsailTypeSpec) return mainsailTypeSpec.Value;
                    if ("value" in mainsailTypeSpec) return mainsailTypeSpec.value;

                    return "N/A";
                  })()}
                </span>
              </div>
              <div className="flex flex-col text-base">
                <span>Security deposit</span>
                <span className="font-medium">TODO: restore this</span>
              </div>
            </div>
          </div>
          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Basic details`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Location details</h4>
            <h5 className="text-lg font-medium ">Country</h5>
            <span className="text-base  mb-1">{yacht.location?.town?.country?.names}</span>
            <h5 className="text-lg  font-medium ">Town</h5>
            <span className="text-base  mb-1">{yacht.location?.town?.names}</span>
            <h5 className="text-lg  font-medium ">Marina</h5>
            <span className="text-base ">{yacht.location?.names}</span>
            <span className="text-sm ">{yacht.location?.address}</span>
          </div>
          <Separator />
          <div className="flex flex-col relative">
            <Button variant="link" className="absolute top-0 right-0" asChild>
              <Link to={`/yachts/${yacht.id}?step=Basic details`}>Edit</Link>
            </Button>
            <h4 className="text-xl font-semibold mb-4">Available charter types</h4>
            <span className="text-base font-medium">{yacht.type}</span>
          </div>
          <Separator />
          <div className="">
            <h4 className="text-xl font-semibold mb-4">Availability</h4>
            <div className="flex gap-2 overflow-x-auto w-full">
              {availability.map((av, index) => {
                return (
                  <div key={index} className="flex flex-col bg-slate-200 rounded-[6px] w-[186px] p-2 items-center">
                    <span className="text-sm w-max">
                      {formatDateUTC(av.periodStart)} - {formatDateUTC(av.periodEnd)}
                    </span>
                    <span className={cn("capitalize mt-auto text-yellow-600", av.available && "text-green-600")}>
                      {av.available ? "Available" : "Reserverd"}
                    </span>
                    <span className="text-sm font-medium">{av.clientPrice} EUR</span>
                  </div>
                );
              })}
            </div>
          </div>
          <Separator />
          <div className="flex flex-col">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>
                  <h4 className="text-xl font-semibold">Operator - Broker notes</h4>
                </AccordionTrigger>
                <AccordionContent className="mt-4">
                  {yacht.operator?.notes ? yacht.operator?.notes : "No data"}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <Separator />
          <div className="flex flex-col">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>
                  <h4 className="text-xl font-semibold">Yacht - Broker notes</h4>
                </AccordionTrigger>
                <AccordionContent className="mt-4">{yacht.notes ? yacht.notes : "No data"}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <Separator />
          <Button className="flex items-center gap-2 text-black text-base self-start pl-0" variant="link">
            Booking Sheet <ExternalLink />
          </Button>
          <Separator />
          <Button className="flex items-center gap-2 text-black text-base self-start pl-0" variant="link">
            Our bookings <ExternalLink />
          </Button>
        </div>
        <DialogFooter className="flex mt-8 w-full !justify-between">
          <Button onClick={() => setOpen(false)} variant="outline">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function evalExtraPriceSimplified(formula: string, loc: string, clientPrice: number): string {
  const price: number = (function (location, duration, clientPrice, listPrice, people, varies) {
    const checkName = (a: string[], v: string) => v.split("\n").some((n) => a.includes(n));
    const fn = eval(`(startLocation, duration, people, clientPrice, listPrice, varies) => {\n\n${formula}\n\n};`);
    const res = fn(location, duration, people, clientPrice, listPrice, varies);
    return res;
  })(loc, 7, clientPrice, clientPrice, 2, 1);

  if (!price) {
    return "varying price";
  }

  return `about ${price.toFixed(2)}`;
}
